//-----------//
// CONSTANTS //
//-----------//

const PRICES = {
  194: {
    34: {
      white: 129,
      premium: 149,
    },
    64: {
      white: 149,
      premium: 179,
    },
    94: {
      white: 169,
      premium: 199,
    },
  },
  232: {
    34: {
      white: 149,
      premium: 179,
    },
    64: {
      white: 169,
      premium: 199,
    },
    94: {
      white: 199,
      premium: 219,
    },
  },
  tablet: {
    34: {
      white: 15,
      premium: 22,
    },
    64: {
      white: 18,
      premium: 32,
    },
    94: {
      white: 22,
      premium: 44,
    },
  },
  pandering: {
    34: {
      classic: 14,
    },
    64: {
      classic: 18,
    },
    94: {
      classic: 22,
    },
  },
  drawer: {
    34: {
      white: 54,
      premium: 56,
    },
    64: {
      white: 58,
      premium: 62,
    },
    94: {
      white: 64,
      premium: 72,
    },
  },
};

//--------------------//
// EXPORTED FUNCTIONS //
//--------------------//

/**
 *
 * @param {Array} configuration
 */
export function calculateConfigurationPrice(configuration) {
  let price = 0;

  for (const column of configuration) {
    price += PRICES[column.box.height][column.box.width][isColorPremium(column.box.color)];

    for (const accessory of column.content) {
      price += PRICES[accessory.name][column.box.width][accessory.name === 'pandering' ? 'classic' : isColorPremium(accessory.color)];
    }
  }

  return price;
}

/**
 * Create a shorten URL to share a given configuration
 * @param {String} baseWebsiteURL The root URL of the website
 * @param {Array} configuration The configuration to encode
 * @returns {String} The complete URL to be shared
 */
export function encodeURL(baseWebsiteURL, configuration) {
  return baseWebsiteURL + '/configurator?share=' + simplifyConfigurationV1(configuration);
}

/**
 * Decode a configuration array from a URL
 * @param {String} url The URL string to decode
 * @returns {Array} The decoded configuration
 */

/**
 * Parse the URL and return the configuration defined in the compressed parameter
 * @param {String} queryString The full query string
 * @returns {Array} The configuration to restore
 */
export function decodeURL(queryString) {
  // If no query string is defined, return an empty configuration
  if (queryString == null || queryString.length === 0) {
    return [];
  }

  const urlParams = new URLSearchParams(queryString);

  // If the query string has no parameter 'share', return an empty configuration
  if (urlParams.get('share') == null) {
    return [];
  }

  // Parse the given configuration
  return parseSimplifiedConfigurationV1(urlParams.get('share'));
}

//--------------------//
// INTERNAL FUNCTIONS //
//--------------------//

/**
 * Returns whether the color is premium or not (premium is more expansive)
 * @param {Number} colorID The ID of the color
 * @returns {Boolean}
 */
function isColorPremium(colorID) {
  return colorID === 5 ? 'white' : 'premium'; // "Blanc" (ID 5) is less expansive.
}

/**
 * Pad a number with zeros on the left
 * @param value The value to pad
 * @param numberOfZeros The number of zeros to pad the string with
 * @returns a string.
 */
function padZeros(value, numberOfZeros) {
  let str = '';

  for (let i = 0; i < numberOfZeros; i++) {
    str += '0';
  }

  return (str + value).slice(-numberOfZeros);
}

/**
 * Given an accessory name, return a single character representing the string
 * @param {String} name The name of the accessory
 * @returns {String} The first letter of the name of the accessory
 */
function getAccessoryShortenName(name) {
  if (name === 'tablet') {
    return 't';
  } else if (name === 'drawer') {
    return 'd';
  } else if (name === 'pandering') {
    return 'p';
  }

  return 'u'; // unknown/undefined
}

function getAccessoryExtendedName(name) {
  if (name === 't') {
    return 'tablet';
  } else if (name === 'd') {
    return 'drawer';
  } else if (name === 'p') {
    return 'pandering';
  }

  return ''; // unknown/undefined
}

/**
 * Compress the given configuration to a very compressed string
 * @param {Array} configuration The configuration to be lightened
 * @returns {String} The short string representing the configuration
 */
function simplifyConfigurationV1(configuration) {
  let str = '';

  for (const column of configuration) {
    str += padZeros(column.position, 2); // position
    str += padZeros(column.box.height, 3); // height
    str += padZeros(column.box.width, 3); // width
    str += padZeros(column.box.color, 3); // color ID
    str += column.isBox ? 1 : 0;

    for (const accessory of column.content) {
      str += padZeros(accessory.id, 2); // id
      str += padZeros(accessory.height, 3); // height
      str += getAccessoryShortenName(accessory.name); // name
      str += padZeros(accessory.color, 3); // color ID
    }

    str += '-';
  }

  return str.substring(0, str.length - 1);
}

/**
 * Re-create a full configuration array from a URL string, by parsing the payload
 * @param {Array} simplifiedConfiguration The lightened array to parse
 * @returns {Array} The parsed configuration array
 */
function parseSimplifiedConfigurationV1(simplifiedConfiguration) {
  /**
   * Internal function to create an Object from an encoded string
   * @param {String} str The encoded string to parse
   * @returns An object representing a box
   */
  function parseBoxFromString(str) {
    return {
      box: {
        color: parseInt(str.substring(8, 11)),
        height: parseInt(str.substring(2, 5)),
        width: parseInt(str.substring(5, 8)),
      },
      content: [],
      isBox: parseInt(str.substring(11, 12)) === 1,
      position: parseInt(str.substring(0, 2)),
    };
  }

  /**
   * Internal function to create an Object from an encoded string
   * @param {String} str The encoded string to parse
   * @returns An object representing an accessory
   */
  function parseAccessoryFromString(str) {
    return {
      color: parseInt(str.substring(6, 9)),
      height: parseInt(str.substring(2, 5)),
      id: parseInt(str.substring(0, 2)),
      name: getAccessoryExtendedName(str.substring(5, 6)),
    };
  }

  let configurations = [];

  let parseCursor = 0;
  while (parseCursor < simplifiedConfiguration.length) {
    // Parse a box
    let configuration = parseBoxFromString(simplifiedConfiguration.substring(parseCursor, parseCursor + 12));

    // Move the cursor to the right of the string
    parseCursor += 12;

    // If the box has no accessories, break the loop
    if (parseCursor >= simplifiedConfiguration.length) {
      configurations.push(configuration);
      break;
    }

    // Start parsing parsing the accessories list since the separator tag has not been met yet
    while (simplifiedConfiguration[parseCursor] !== '-') {
      configuration.content.push(parseAccessoryFromString(simplifiedConfiguration.substring(parseCursor, parseCursor + 9)));

      // Move the cursor to the right of the string
      parseCursor += 9;

      // If the box has no accessories, break the loop
      if (parseCursor >= simplifiedConfiguration.length) {
        break;
      }
    }

    parseCursor += 1; // Go to the character right after the separator
    // If the box has no accessories, break the loop
    if (parseCursor >= simplifiedConfiguration.length) {
      configurations.push(configuration);
      break;
    }

    configurations.push(configuration);
  }

  return configurations;
}
